// Phase 138.B — `/try` free resource upload API client.
//
// Generation runs as an async backend job: POST upload-and-generate
// returns `{job_id, lead_id, status:"running"}` immediately, and the
// frontend polls `/api/try/jobs/{job_id}` until status flips to
// `done` or `error`. Avoids Cloudflare 502s on the long single
// connection the synchronous version held open.
import { api } from "./api";

export const getTryStatus = () =>
  api.get("/try/status").then((r) => r.data);

const POLL_INITIAL_DELAY_MS = 3000;
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Cloudflare and other edge proxies sometimes intercept a 5xx with an
// HTML error page. Detect that so the UI can show a friendly message
// instead of dumping raw HTML into the form.
const _looksLikeHtml = (value) => {
  if (typeof value !== "string") return false;
  const head = value.trim().slice(0, 200).toLowerCase();
  return head.startsWith("<!doctype") || head.startsWith("<html");
};

const _friendlyError = (err) => {
  const data = err?.response?.data;
  const detail = typeof data === "object" ? data?.detail : data;
  if (!detail) {
    return err?.message || "Something went wrong building the lesson.";
  }
  if (_looksLikeHtml(detail)) {
    return "Our lesson builder took too long to respond. Please try again.";
  }
  return String(detail);
};

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const _pollTryJob = async (jobId) => {
  const startedAt = Date.now();
  await _sleep(POLL_INITIAL_DELAY_MS);
  let consecutiveErrors = 0;
  while (true) {
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      throw new Error("Generation timed out after 5 minutes.");
    }
    try {
      const { data: job } = await api.get(
        `/try/jobs/${encodeURIComponent(jobId)}`,
        { timeout: 20000 }
      );
      consecutiveErrors = 0;
      if (job.status === "done") return job;
      if (job.status === "error") {
        const e = new Error(job.error || "Generation failed.");
        e.status_code = job.status_code;
        throw e;
      }
    } catch (err) {
      // If the job itself returned error status, re-throw.
      if (err?.status_code) throw err;
      consecutiveErrors += 1;
      if (consecutiveErrors >= 3) throw err;
    }
    await _sleep(POLL_INTERVAL_MS);
  }
};

export const uploadAndGenerateTry = async (file, grade, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  form.append("grade", grade);
  let started;
  try {
    const { data } = await api.post("/try/upload-and-generate", form, {
      timeout: 30000,
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    });
    started = data;
  } catch (err) {
    const friendly = _friendlyError(err);
    const wrapped = new Error(friendly);
    wrapped.response = err?.response;
    throw wrapped;
  }

  if (!started?.job_id) {
    throw new Error("Server did not return a job id.");
  }
  if (onProgress) onProgress(100);
  try {
    return await _pollTryJob(started.job_id);
  } catch (err) {
    const friendly = _friendlyError(err);
    const wrapped = new Error(friendly);
    wrapped.response = err?.response;
    throw wrapped;
  }
};

export const trackTryTrialClick = (leadId) =>
  api
    .post("/try/track-trial-click", { lead_id: leadId })
    .then((r) => r.data)
    .catch(() => null); // fire-and-forget — never block the trial CTA

export const getTryLessonForLead = (leadId) =>
  api.get(`/try/lead/${encodeURIComponent(leadId)}/lesson`).then((r) => r.data);
